import { PluginFunc } from "dayjs";

/**
 * A dayjs plugin that overrides toJSON() and toISOString() to return
 * local time strings in ISO format (YYYY-MM-DDTHH:mm:ss) without UTC conversion.
 *
 * This is useful when working with APIs that expect date-only or local datetime
 * values without timezone shifts. For example, when sending a date like "2026-01-20"
 * to an API, this prevents it from being converted to UTC and potentially shifting
 * to the previous day in timezones ahead of UTC.
 *
 * Usage:
 *   import dayjs from "dayjs";
 *   import localISOString from "utils/localISOString";
 *   dayjs.extend(localISOString);
 *
 *   const date = dayjs("2026-01-20");
 *   date.toISOString(); // "2026-01-20T00:00:00" (no Z suffix, no UTC conversion)
 */
const plugin: PluginFunc = (_option, dayjsClass) => {
  const proto = dayjsClass.prototype;

  proto.toJSON = function () {
    return this.format("YYYY-MM-DDTHH:mm:ss");
  };

  proto.toISOString = function () {
    return this.format("YYYY-MM-DDTHH:mm:ss");
  };
};

export default plugin;

declare module "dayjs" {
  export interface Dayjs {
    /**
     * Returns the date formatted as a local ISO string (YYYY-MM-DDTHH:mm:ss)
     * without UTC conversion.
     */
    toISOString(): string;

    /**
     * Returns the date formatted as a local ISO string (YYYY-MM-DDTHH:mm:ss)
     * without UTC conversion for JSON serialization.
     */
    toJSON(): string;
  }
}
